import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Editor from "@/components/Editor";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import BlockEditor from "@/components/BlockEditor";
import TagMultiSelect from "@/components/TagMultiSelect";
import TaxonomyManager from "@/components/TaxonomyManager";
import CourseContextBar, { colorVar } from "@/components/CourseContextBar";
import ExplorerModal from "@/components/ExplorerModal";
import { useKernel } from "@/kernel/useKernel";
import type { Scaffold } from "@/kernel/KernelContext";
import { getStepPack } from "@/api/packs";
import { useToast } from "@/components/Toast";
import { useAuth } from "@/hooks/useAuth";
import { listMyCourses } from "@/api/course";
import { useConfirm } from "@/components/ConfirmDialog";
import { useDebouncedEffect } from "@/hooks/useDebouncedEffect";
import { useTheme } from "@/theme/ThemeContext";
import {
  listInstructorExercises,
  getInstructorExercise,
  createExercise,
  updateExercise,
  deleteExercise,
  duplicateExercise,
  reorderExercises,
  createStep,
  updateStep,
  deleteStep,
  duplicateStep,
  reorderSteps,
  listChapters,
  createChapter,
  updateChapter,
  reorderChapters,
  listDegrees,
} from "@/api/exercises";
import { listPacks } from "@/api/packs";
import type { InstructorExercise, InstructorStep, Chapter, Degree, Course, GamePack } from "@/types";

// ─── Page principale (scopée à un cours via l'URL) ─────────────────────────────

export default function Instructor() {
  const navigate = useNavigate();
  const { courseId: courseIdParam } = useParams<{ courseId: string }>();
  const courseId = Number(courseIdParam);
  // Deep-link : si on arrive avec ?serie=<id>, on auto-sélectionne la série après
  // chargement (cf. effet plus bas). Consommé une seule fois.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkSerieId = searchParams.get("serie");
  const confirm = useConfirm();
  const [courses, setCourses]     = useState<Course[]>([]);
  const [exercises, setExercises] = useState<InstructorExercise[]>([]);
  const [chapters, setChapters]   = useState<Chapter[]>([]);
  const [degrees, setDegrees]     = useState<Degree[]>([]);
  // Packs de jeu du cours (gamelib P1.5) — affichés en dropdown dans StepCard pour
  // attacher un pack à une étape. Tolérant à l'erreur : si l'endpoint manque, []
  // (les éditeurs continuent de marcher sans la section pack).
  const [packs, setPacks]         = useState<GamePack[]>([]);
  const [selected, setSelected]   = useState<InstructorExercise | null>(null);
  const [showTaxonomy, setShowTaxonomy] = useState(false);
  const [showExplorer, setShowExplorer] = useState(false);

  const current = courses.find((c) => c.id === courseId) ?? null;

  // Liste des cours (indépendante du cours courant) — pour la barre de contexte.
  const reloadCourses = useCallback(async () => {
    setCourses(await listMyCourses());
  }, []);

  // Contenu scopé au cours de l'URL.
  const reload = useCallback(async () => {
    const [exs, chs, degs, pks] = await Promise.all([
      listInstructorExercises(courseId), listChapters(courseId), listDegrees(courseId),
      listPacks(courseId).catch(() => [] as GamePack[]),
    ]);
    setExercises(exs);
    setChapters(chs);
    setDegrees(degs);
    setPacks(pks);
  }, [courseId]);

  useEffect(() => { reloadCourses(); }, [reloadCourses]);
  // Recharge le contenu (et vide la sélection) à chaque changement de cours.
  useEffect(() => { setSelected(null); reload(); }, [reload]);

  // Rafraîchit le contenu (séries + packs de jeu) quand l'onglet reprend le focus.
  // Un import JSON ou la création d'un pack se fait sur la fiche cours — souvent dans
  // un autre onglet : sans ça, le nouveau pack n'apparaît dans le menu « Pack de jeu »
  // qu'après un rechargement manuel de la page. reload() ne touche pas la sélection
  // courante, l'édition en cours n'est donc pas perturbée.
  useEffect(() => {
    const onFocus = () => { reload(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [reload]);

  const selectExercise = async (id: number) => setSelected(await getInstructorExercise(id));

  // Deep-link `?serie=<id>` : auto-sélectionne la série à l'arrivée (une seule fois)
  // dès que la liste est chargée. On nettoie l'URL pour ne pas re-déclencher.
  useEffect(() => {
    if (!deepLinkSerieId || exercises.length === 0) return;
    const id = Number(deepLinkSerieId);
    if (exercises.some((e) => e.id === id)) selectExercise(id);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercises.length, deepLinkSerieId]);

  const handleNew = async () => {
    // Nouvelle série = brouillon sans degré (l'enseignant l'attribue ensuite).
    const { id } = await createExercise({ title: "Nouvelle série", course_id: courseId });
    await reload();
    selectExercise(id);
  };

  const handleDuplicate = async (id: number) => {
    const { id: cloneId } = await duplicateExercise(id);
    await reload();
    selectExercise(cloneId);
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: "Supprimer cette série ?",
      message: "La série et tous ses exercices seront définitivement supprimés.",
      confirmLabel: "Supprimer",
      danger: true,
    });
    if (!ok) return;
    await deleteExercise(id);
    if (selected?.id === id) setSelected(null);
    reload();
  };

  const handleRename = async (id: number, title: string) => {
    await updateExercise(id, { title });
    setExercises((prev) => prev.map((e) => (e.id === id ? { ...e, title } : e)));
    setSelected((prev) => (prev && prev.id === id ? { ...prev, title } : prev));
  };

  const handleReorder = async (order: number[]) => {
    setExercises((prev) => order.map((id) => prev.find((e) => e.id === id)!).filter(Boolean));
    await reorderExercises(courseId, order);
  };

  // Chapitres : réordonner (ordre global, renumérotation auto), renommer, masquer.
  const handleReorderChapters = async (order: number[]) => {
    setChapters((prev) => order.map((id, i) => {
      const c = prev.find((x) => x.id === id);
      return c ? { ...c, order: i } : c;
    }).filter(Boolean) as Chapter[]);
    await reorderChapters(courseId, order).catch(() => listChapters(courseId).then(setChapters));
  };
  const handleRenameChapter = async (id: number, title: string) => {
    setChapters((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
    await updateChapter(id, { title }).catch(() => listChapters(courseId).then(setChapters));
  };
  const handleToggleChapter = async (id: number) => {
    let next = true;
    setChapters((prev) => prev.map((c) => {
      if (c.id !== id) return c;
      next = c.visible === false; // masqué → afficher, visible → masquer
      return { ...c, visible: next };
    }));
    await updateChapter(id, { visible: next }).catch(() => listChapters(courseId).then(setChapters));
  };

  // Accent couleur du cours courant → recolore les boutons primaires de la page.
  const accentStyle = { "--accent": current ? colorVar(current.color) : "var(--blue)" } as React.CSSProperties;

  return (
    <div style={{ ...styles.page, ...accentStyle }}>
      {/* Fil d'ariane (continuité avec l'espace enseignant) + actions de l'éditeur.
          La barre haute globale (thème / apps / compte) est fournie par InstructorShell. */}
      <div style={styles.toolbar}>
        <nav style={styles.crumbs} aria-label="Fil d'ariane">
          <button style={styles.crumbLink} onClick={() => navigate("/instructor/courses")} type="button">Cours</button>
          <Icon name="next" size={12} style={{ color: "var(--text-faint)", flexShrink: 0 }} />
          <button style={styles.crumbLink} onClick={() => navigate(`/instructor/courses/${courseId}`)} type="button">
            {current?.title ?? "Cours"}
          </button>
          <Icon name="next" size={12} style={{ color: "var(--text-faint)", flexShrink: 0 }} />
          <span style={styles.crumbCurrent}>Édition</span>
        </nav>
        <span style={{ flex: 1 }} />
        <button style={styles.manageBtn} onClick={() => setShowExplorer(true)} type="button">
          <Icon name="search" size={15} />
          Explorer
        </button>
        <button style={styles.manageBtn} onClick={() => setShowTaxonomy(true)} type="button">
          <Icon name="panel" size={15} />
          Degrés &amp; chapitres
        </button>
      </div>

      <CourseContextBar courses={courses} current={current} onManage={() => navigate(`/instructor/courses/${courseId}`)} />

      {showTaxonomy && (
        <TaxonomyManager
          courseId={courseId}
          degrees={degrees}
          chapters={chapters}
          // Refetch à la fermeture : un renommage/suppression dans la modale (source de
          // vérité) ne doit jamais laisser un nom obsolète dans le TagMultiSelect en dessous.
          onClose={() => { setShowTaxonomy(false); reload(); }}
          onChanged={reload}
        />
      )}
      {showExplorer && (
        <ExplorerModal
          currentCourseId={courseId}
          onClose={() => setShowExplorer(false)}
          // On garde le panneau ouvert (copies multiples) ; on rafraîchit juste la
          // sidebar pour que les imports apparaissent dans le cours courant.
          onCopied={() => reload()}
        />
      )}

      <div style={styles.body}>
        <Sidebar
          exercises={exercises}
          degrees={degrees}
          chapters={chapters}
          selectedId={selected?.id ?? null}
          onSelect={selectExercise}
          onNew={handleNew}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          onRename={handleRename}
          onReorder={handleReorder}
          onReorderChapters={handleReorderChapters}
          onRenameChapter={handleRenameChapter}
          onToggleChapter={handleToggleChapter}
        />
        <main style={styles.main}>
          {selected ? (
            <ExerciseEditor
              key={selected.id}
              exercise={selected}
              chapters={chapters}
              degrees={degrees}
              packs={packs}
              onChapterCreated={async (title) => {
                const ch = await createChapter(courseId, title);
                setChapters((prev) => [...prev, ch]);
                return ch;
              }}
              onManageTaxonomy={() => setShowTaxonomy(true)}
              onSettingsSaved={async (data) => {
                // Léger (pas de reload complet) : compatible avec l'enregistrement auto.
                const res = await updateExercise(selected.id, data);
                // Résout les ids en objets pour garder la barre latérale (tags) à jour,
                // et aligne `published` sur l'état réel renvoyé (garde-fou orphelin).
                const degObjs = data.degree_ids
                  ? degrees.filter((d) => data.degree_ids!.includes(d.id))
                  : undefined;
                const chObjs = data.chapter_ids
                  ? chapters.filter((c) => data.chapter_ids!.includes(c.id))
                  : undefined;
                const patch = {
                  ...data,
                  published: res.published,
                  ...(degObjs ? { degrees: degObjs } : {}),
                  ...(chObjs ? { chapters: chObjs } : {}),
                };
                setSelected((prev) => (prev ? { ...prev, ...patch } : prev));
                setExercises((prev) =>
                  prev.map((e) => (e.id === selected.id ? { ...e, ...patch } : e)));
              }}
              onStepsChanged={async () => setSelected(await getInstructorExercise(selected.id))}
            />
          ) : (
            <div style={styles.emptyState}>
              <Icon name="checklist" size={40} style={{ color: "var(--text-faint)" }} />
              {exercises.length === 0 ? (
                <>
                  <p style={styles.emptyTitle}>Aucune série dans ce cours</p>
                  <p style={styles.emptyHint}>Crée ta première série pour commencer à ajouter des exercices.</p>
                  <Button variant="primary" onClick={handleNew}>+ Créer une série</Button>
                </>
              ) : (
                <>
                  <p style={styles.emptyTitle}>Aucune série sélectionnée</p>
                  <p style={styles.emptyHint}>Choisis une série dans la liste de gauche pour l'éditer.</p>
                </>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ─── Barre latérale (liste triable + renommage en ligne) ───────────────────────

function Sidebar({
  exercises, degrees, chapters, selectedId, onSelect, onNew, onDuplicate, onDelete, onRename, onReorder,
  onReorderChapters, onRenameChapter, onToggleChapter,
}: {
  exercises: InstructorExercise[];
  degrees: Degree[];
  chapters: Chapter[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
  onDuplicate: (id: number) => void;
  onDelete: (id: number) => void;
  onRename: (id: number, title: string) => void;
  onReorder: (order: number[]) => void;
  onReorderChapters: (order: number[]) => void;
  onRenameChapter: (id: number, title: string) => void;
  onToggleChapter: (id: number) => void;
}) {
  const [search, setSearch]   = useState("");
  const [query, setQuery]     = useState("");           // version temporisée
  const [degF, setDegF]       = useState<Set<number>>(new Set());
  const [chF, setChF]         = useState<Set<number>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  // Vue « Par chapitre » (accordéons) vs « Liste » (plate, triable). Par défaut groupée.
  const [groupView, setGroupView] = useState(true);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleCollapse = (key: string) =>
    setCollapsed((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  // Temporise la recherche (~300 ms) pour ne pas re-filtrer à chaque frappe.
  useDebouncedEffect(() => setQuery(search), [search], 300);

  const toggle = (set: Set<number>, setter: (s: Set<number>) => void, id: number) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  };

  const q = query.trim().toLowerCase();
  const filtersActive = q.length > 0 || degF.size > 0 || chF.size > 0;
  const grouped = chapters.length > 0 && groupView;
  const filtered = exercises.filter((ex) =>
    (!q || ex.title.toLowerCase().includes(q)) &&
    (degF.size === 0 || ex.degrees.some((d) => degF.has(d.id))) &&
    (chF.size === 0 || ex.chapters.some((c) => chF.has(c.id))),
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  // Réordre global (vue Liste). Désactivé pendant un filtre (l'ordre d'un
  // sous-ensemble affiché serait ambigu).
  const onDragEnd = (e: DragEndEvent) => {
    if (filtersActive) return;
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = exercises.findIndex((x) => x.id === active.id);
    const newIdx = exercises.findIndex((x) => x.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onReorder(arrayMove(exercises, oldIdx, newIdx).map((x) => x.id));
  };

  // Réordre AU SEIN d'un chapitre (vue groupée). On réordonne les membres du
  // groupe aux positions qu'ils occupent déjà dans l'ordre global (les autres
  // séries ne bougent pas). L'ordre global reste unique : réordonner une série
  // multi-chapitres la repositionne aussi dans ses autres chapitres (cohérent
  // avec le modèle « une série = un seul ordre »). Ids composites `clé:id` car
  // une même série peut être rendue sous plusieurs chapitres (M2M).
  const reorderGroup = (items: InstructorExercise[]) => (e: DragEndEvent) => {
    if (filtersActive) return;
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = items.map((x) => x.id);
    const aId = Number(String(active.id).split(":")[1]);
    const oId = Number(String(over.id).split(":")[1]);
    const oldIdx = ids.indexOf(aId);
    const newIdx = ids.indexOf(oId);
    if (oldIdx < 0 || newIdx < 0) return;
    const newOrder = arrayMove(ids, oldIdx, newIdx);
    const byId = new Map(exercises.map((x) => [x.id, x] as const));
    const positions: number[] = [];
    exercises.forEach((x, i) => { if (ids.includes(x.id)) positions.push(i); });
    const next = exercises.slice();
    positions.forEach((pos, k) => { next[pos] = byId.get(newOrder[k])!; });
    onReorder(next.map((x) => x.id));
  };

  const serieGroups = grouped ? groupSeriesByChapter(filtered, chapters) : null;
  // Numéro de chapitre = position dans l'ordre global (cohérent avec cockpit / vue élève).
  const chapterNumber = new Map(
    [...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((c, i) => [c.id, i + 1]),
  );

  // Réordre des CHAPITRES : on calcule l'ordre global complet (chapitres vides
  // inclus → ils gardent leur place) puis on persiste.
  const onChapterDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const sorted = [...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const oldIdx = sorted.findIndex((c) => c.id === active.id);
    const newIdx = sorted.findIndex((c) => c.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onReorderChapters(arrayMove(sorted, oldIdx, newIdx).map((c) => c.id));
  };

  return (
    <aside style={styles.sidebar}>
      <div style={styles.sidebarHead}>
        <span style={{ color: "var(--blue)", fontWeight: 600 }}>Séries</span>
      </div>
      <Button variant="primary" onClick={onNew} size="sm" style={styles.newBtn}>
        + Nouvelle série
      </Button>

      <div style={styles.searchRow}>
        <Icon name="search" size={14} style={{ color: "var(--text-faint)", flexShrink: 0 }} />
        <input style={styles.searchInput} placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <button
          style={{ ...styles.filterBtn, ...((degF.size || chF.size) ? { color: "var(--blue)", borderColor: "var(--blue)" } : {}) }}
          onClick={() => setShowFilters((v) => !v)}
          title="Filtrer par degré / chapitre"
          type="button"
        >
          <Icon name="more" size={14} />
        </button>
      </div>

      {showFilters && (
        <div style={styles.filters}>
          {degrees.length > 0 && <span style={styles.filterLabel}>Degrés</span>}
          <div style={styles.chipRow}>
            {degrees.map((d) => (
              <button key={d.id} type="button" onClick={() => toggle(degF, setDegF, d.id)}
                      style={{ ...styles.chip, ...(degF.has(d.id) ? styles.chipOn : {}) }}>{d.label}</button>
            ))}
          </div>
          {chapters.length > 0 && <span style={styles.filterLabel}>Chapitres</span>}
          <div style={styles.chipRow}>
            {chapters.map((c) => (
              <button key={c.id} type="button" onClick={() => toggle(chF, setChF, c.id)}
                      style={{ ...styles.chip, ...(chF.has(c.id) ? styles.chipOn : {}) }}>{c.title}</button>
            ))}
          </div>
        </div>
      )}

      {chapters.length > 0 && (
        <div style={styles.viewToggle} role="group" aria-label="Affichage des séries">
          <button type="button" onClick={() => setGroupView(true)}
                  style={{ ...styles.viewBtn, ...(grouped ? styles.viewBtnOn : {}) }}>Par chapitre</button>
          <button type="button" onClick={() => setGroupView(false)}
                  style={{ ...styles.viewBtn, ...(!grouped ? styles.viewBtnOn : {}) }}>Liste</button>
        </div>
      )}

      <div style={styles.exList}>
        {filtered.length === 0 && <p style={styles.empty}>Aucune série.</p>}
        {grouped ? (
          // DndContext externe : réordonne les CHAPITRES (poignée sur l'en-tête).
          // Chaque chapitre contient son propre DndContext interne pour les séries
          // (dnd-kit supporte l'imbrication). Le groupe « Sans chapitre » est rendu
          // à part (non draggable / non renommable).
          <>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onChapterDragEnd}>
              <SortableContext items={serieGroups!.filter((g) => g.chapterId !== null).map((g) => g.chapterId as number)} strategy={verticalListSortingStrategy}>
                {serieGroups!.filter((g) => g.chapterId !== null).map((g) => (
                  <ChapterGroup
                    key={g.key}
                    group={g}
                    chapterId={g.chapterId as number}
                    number={chapterNumber.get(g.chapterId as number) ?? 0}
                    open={!collapsed.has(g.key)}
                    filtersActive={filtersActive}
                    selectedId={selectedId}
                    sensors={sensors}
                    onToggleCollapse={() => toggleCollapse(g.key)}
                    onRenameChapter={onRenameChapter}
                    onToggleChapter={onToggleChapter}
                    onReorderSeries={reorderGroup}
                    onSelect={onSelect}
                    onDuplicate={onDuplicate}
                    onDelete={onDelete}
                    onRename={onRename}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {serieGroups!.filter((g) => g.chapterId === null).map((g) => {
              const open = !collapsed.has(g.key);
              return (
                <div key={g.key} style={styles.chGroup}>
                  <button style={styles.chHead} onClick={() => toggleCollapse(g.key)} aria-expanded={open} type="button">
                    <Icon name={open ? "expanded" : "collapsed"} size={14} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
                    <span style={styles.chTitle}>{g.title}</span>
                    <span style={styles.chCount}>{g.items.length}</span>
                  </button>
                  {open && (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={reorderGroup(g.items)}>
                      <SortableContext items={g.items.map((e) => `${g.key}:${e.id}`)} strategy={verticalListSortingStrategy}>
                        {g.items.map((ex) => (
                          <ExerciseItem
                            key={ex.id}
                            sortId={`${g.key}:${ex.id}`}
                            ex={ex}
                            active={ex.id === selectedId}
                            draggable={!filtersActive}
                            onSelect={() => onSelect(ex.id)}
                            onDuplicate={() => onDuplicate(ex.id)}
                            onDelete={() => onDelete(ex.id)}
                            onRename={(t) => onRename(ex.id, t)}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={filtered.map((e) => e.id)} strategy={verticalListSortingStrategy}>
              {filtered.map((ex) => (
                <ExerciseItem
                  key={ex.id}
                  sortId={ex.id}
                  ex={ex}
                  active={ex.id === selectedId}
                  draggable={!filtersActive}
                  onSelect={() => onSelect(ex.id)}
                  onDuplicate={() => onDuplicate(ex.id)}
                  onDelete={() => onDelete(ex.id)}
                  onRename={(t) => onRename(ex.id, t)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </aside>
  );
}

interface SerieGroup {
  key: string; title: string; order: number; items: InstructorExercise[];
  chapterId: number | null;       // null = groupe « Sans chapitre »
  visible: boolean;
}

/** Regroupe les séries par chapitre pour la vue arborescente de la sidebar (M2M :
 *  une série apparaît sous chacun de ses chapitres ; sans chapitre → « Sans chapitre »).
 *  Titre / ordre / visibilité viennent du prop `chapters` (source de vérité) → un
 *  renommage ou un masquage se reflète sans refetch des exercices. */
function groupSeriesByChapter(list: InstructorExercise[], chapters: Chapter[]): SerieGroup[] {
  const meta = new Map(chapters.map((c) => [c.id, c]));
  const map = new Map<string, SerieGroup>();
  const add = (key: string, ex: InstructorExercise, base: Partial<SerieGroup> & { title: string; order: number; chapterId: number | null }) => {
    let g = map.get(key);
    if (!g) { g = { key, items: [], visible: true, ...base }; map.set(key, g); }
    g.items.push(ex);
  };
  for (const ex of list) {
    if (!ex.chapters || ex.chapters.length === 0) {
      add("none", ex, { title: "Sans chapitre", order: Number.MAX_SAFE_INTEGER, chapterId: null });
    } else for (const c of ex.chapters) {
      const m = meta.get(c.id);
      add(String(c.id), ex, {
        title: m?.title ?? c.title,
        order: m?.order ?? 0,
        chapterId: c.id,
        visible: m?.visible !== false,
      });
    }
  }
  return [...map.values()].sort((a, b) => a.order - b.order);
}

/** En-tête de chapitre draggable (réordre) + numéro auto + renommage en place +
 *  œil de visibilité, suivi du DndContext interne des séries du chapitre. */
function ChapterGroup({
  group, chapterId, number, open, filtersActive, selectedId, sensors,
  onToggleCollapse, onRenameChapter, onToggleChapter, onReorderSeries,
  onSelect, onDuplicate, onDelete, onRename,
}: {
  group: SerieGroup;
  chapterId: number;
  number: number;
  open: boolean;
  filtersActive: boolean;
  selectedId: number | null;
  sensors: ReturnType<typeof useSensors>;
  onToggleCollapse: () => void;
  onRenameChapter: (id: number, title: string) => void;
  onToggleChapter: (id: number) => void;
  onReorderSeries: (items: InstructorExercise[]) => (e: DragEndEvent) => void;
  onSelect: (id: number) => void;
  onDuplicate: (id: number) => void;
  onDelete: (id: number) => void;
  onRename: (id: number, title: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: chapterId });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(group.title);
  const hidden = !group.visible;

  const commit = () => {
    setEditing(false);
    const t = draft.trim();
    if (t && t !== group.title) onRenameChapter(chapterId, t);
    else setDraft(group.title);
  };

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform), transition,
    opacity: isDragging ? 0.6 : 1, ...styles.chGroup,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{ ...styles.chHead, ...(hidden ? { opacity: 0.5 } : {}) }}>
        <button style={styles.chDragHandle} {...attributes} {...listeners} aria-label="Déplacer le chapitre" type="button"><DragDots /></button>
        <button style={styles.chCollapseBtn} onClick={onToggleCollapse} aria-expanded={open} aria-label={open ? "Replier" : "Déplier"} type="button">
          <Icon name={open ? "expanded" : "collapsed"} size={14} style={{ color: "var(--text-muted)" }} />
        </button>
        <span style={styles.chNum}>{number}</span>
        {editing ? (
          <input autoFocus style={styles.chTitleInput} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
                 onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(group.title); setEditing(false); } }} />
        ) : (
          <span style={styles.chTitle} onClick={onToggleCollapse} onDoubleClick={() => setEditing(true)} title="Double-clic pour renommer">{group.title}</span>
        )}
        <span style={styles.chCount}>{group.items.length}</span>
        <button style={styles.chEye} onClick={() => onToggleChapter(chapterId)} type="button"
                aria-label={hidden ? "Afficher le chapitre" : "Masquer le chapitre"}
                title={hidden ? "Chapitre masqué des élèves — afficher" : "Chapitre visible — masquer"}>
          <Icon name={hidden ? "eye-off" : "eye"} size={15} />
        </button>
      </div>
      {open && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onReorderSeries(group.items)}>
          <SortableContext items={group.items.map((e) => `${group.key}:${e.id}`)} strategy={verticalListSortingStrategy}>
            {group.items.map((ex) => (
              <ExerciseItem
                key={ex.id}
                sortId={`${group.key}:${ex.id}`}
                ex={ex}
                active={ex.id === selectedId}
                draggable={!filtersActive}
                onSelect={() => onSelect(ex.id)}
                onDuplicate={() => onDuplicate(ex.id)}
                onDelete={() => onDelete(ex.id)}
                onRename={(t) => onRename(ex.id, t)}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function ExerciseItem({
  ex, sortId, active, draggable, onSelect, onDuplicate, onDelete, onRename,
}: {
  ex: InstructorExercise;
  sortId: string | number;
  active: boolean;
  draggable: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onRename: (title: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: sortId });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(ex.title);
  const [menuOpen, setMenuOpen] = useState(false);
  // Ouvre le menu vers le HAUT quand la série est en bas de la liste scrollable
  // (sinon l'unique entrée « Supprimer » sort de l'écran — il fallait scroller).
  const [menuUp, setMenuUp] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    if (!menuOpen && menuRef.current) {
      const r = menuRef.current.getBoundingClientRect();
      setMenuUp(window.innerHeight - r.bottom < 80); // ~ hauteur d'une entrée + marge
    }
    setMenuOpen((o) => !o);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [menuOpen]);

  const commit = () => {
    setEditing(false);
    const t = draft.trim();
    if (t && t !== ex.title) onRename(t);
    else setDraft(ex.title);
  };

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    ...styles.exItem,
    ...(active ? styles.exItemActive : {}),
  };

  return (
    <div ref={setNodeRef} style={style}>
      {draggable ? (
        <button style={styles.exHandle} {...attributes} {...listeners} aria-label="Déplacer" type="button">
          <DragDots />
        </button>
      ) : (
        // Réordre indisponible (vue par chapitre ou filtre actif) : espace invisible
        // pour garder l'alignement, sans afficher de poignée qui suggère un drag possible.
        <span style={{ ...styles.exHandle, visibility: "hidden" }} aria-hidden="true"><DragDots /></span>
      )}
      {editing ? (
        <input
          autoFocus
          style={styles.renameInput}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(ex.title); setEditing(false); } }}
        />
      ) : (
        <span style={styles.exTitle} onClick={onSelect} onDoubleClick={() => setEditing(true)} title="Double-clic pour renommer">
          {ex.title}
          {!ex.published && <span style={styles.draft}> brouillon</span>}
        </span>
      )}
      <button style={styles.exAction} onClick={() => setEditing(true)} aria-label="Renommer" title="Renommer" type="button">
        <Icon name="rename" size={15} />
      </button>
      <button style={styles.exAction} onClick={onDuplicate} aria-label="Dupliquer" title="Dupliquer" type="button">
        <Icon name="duplicate" size={15} />
      </button>
      {/* Action destructrice isolée dans un menu « … » → évite les suppressions accidentelles. */}
      <div ref={menuRef} style={{ position: "relative", display: "inline-flex" }}>
        <button style={styles.exAction} onClick={toggleMenu} aria-haspopup="menu" aria-expanded={menuOpen} aria-label="Plus d'actions" title="Plus d'actions" type="button">
          <Icon name="more" size={15} />
        </button>
        {menuOpen && (
          <div style={{ ...styles.exMenu, ...(menuUp ? { top: "auto", bottom: "calc(100% + 4px)" } : {}) }} role="menu">
            <button style={styles.exMenuDanger} role="menuitem" type="button"
                    onClick={() => { setMenuOpen(false); onDelete(); }}>
              <Icon name="trash" size={14} /> Supprimer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Éditeur d'exercice (réglages + étapes en accordéon) ───────────────────────

function ExerciseEditor({
  exercise, chapters, degrees, packs, onChapterCreated, onSettingsSaved, onStepsChanged, onManageTaxonomy,
}: {
  exercise: InstructorExercise;
  chapters: Chapter[];
  degrees: Degree[];
  packs: GamePack[];
  onChapterCreated: (title: string) => Promise<Chapter>;
  onSettingsSaved: (data: Partial<InstructorExercise>) => Promise<void>;
  onStepsChanged: () => Promise<void>;
  onManageTaxonomy: () => void;
}) {
  const [title, setTitle]           = useState(exercise.title);
  const [desc, setDesc]             = useState(exercise.description);
  const [degreeIds, setDegreeIds]   = useState<number[]>(exercise.degree_ids ?? []);
  const [chapterIds, setChapterIds] = useState<number[]>(exercise.chapter_ids ?? []);
  const [published, setPublished]   = useState(exercise.published);

  const toggleDegree = (id: number) =>
    setDegreeIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  // Étapes : source de vérité locale (les StepCard sont contrôlés).
  const [steps, setSteps]   = useState<InstructorStep[]>(exercise.steps);
  const [open, setOpen]     = useState<Set<number>>(new Set());

  const confirm = useConfirm();

  // Indicateur d'enregistrement automatique partagé (réglages + étapes).
  const [status, setStatus] = useState<"idle" | "saving" | "saved">("idle");
  const track = useCallback(async (p: Promise<unknown>) => {
    setStatus("saving");
    try { await p; setStatus("saved"); } catch { setStatus("idle"); }
  }, []);

  // Enregistrement auto des réglages de l'exercice (700 ms après la dernière frappe).
  useDebouncedEffect(() => {
    track(onSettingsSaved({
      title, description: desc, degree_ids: degreeIds, chapter_ids: chapterIds, published,
    }));
  }, [title, desc, degreeIds, chapterIds, published]);

  const patchStep = (id: number, patch: Partial<InstructorStep>) =>
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const addStep = async () => {
    await createStep(exercise.id, {});
    await onStepsChanged();
    const refreshed = await getInstructorExercise(exercise.id);
    setSteps(refreshed.steps);
    const last = refreshed.steps[refreshed.steps.length - 1];
    if (last) setOpen((o) => new Set(o).add(last.id));
  };

  const removeStep = async (id: number) => {
    const ok = await confirm({
      title: "Supprimer cet exercice ?",
      message: "Le contenu de l'exercice sera définitivement supprimé.",
      confirmLabel: "Supprimer",
      danger: true,
    });
    if (!ok) return;
    await deleteStep(id);
    setSteps((prev) => prev.filter((s) => s.id !== id));
  };

  const dupStep = async (id: number) => {
    await duplicateStep(id);
    const refreshed = await getInstructorExercise(exercise.id);
    setSteps(refreshed.steps);
  };

  const reorder = async (order: number[]) => {
    setSteps((prev) => order.map((id) => prev.find((s) => s.id === id)!).filter(Boolean));
    await reorderSteps(exercise.id, order);
  };

  const moveStep = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= steps.length) return;
    reorder(arrayMove(steps, index, next).map((s) => s.id));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = steps.findIndex((s) => s.id === active.id);
    const newIdx = steps.findIndex((s) => s.id === over.id);
    reorder(arrayMove(steps, oldIdx, newIdx).map((s) => s.id));
  };

  const allOpen = open.size === steps.length && steps.length > 0;
  const toggleAll = () =>
    setOpen(allOpen ? new Set() : new Set(steps.map((s) => s.id)));
  const toggleOne = (id: number) =>
    setOpen((o) => { const n = new Set(o); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div style={styles.editor}>
      <div style={styles.editorHead}>
        <h2 style={styles.h2}>Modifier la série</h2>
        <span style={{ flex: 1 }} />
        <SaveStatus status={status} />
        <button
          style={styles.previewBtn}
          onClick={() => window.open(`/exercises/${exercise.id}`, "_blank", "noopener")}
          type="button"
          title="Ouvrir la vue élève dans un nouvel onglet"
        >
          <Icon name="search" size={14} /> Aperçu élève
        </button>
        <button
          style={{ ...styles.pubToggle, ...(published ? styles.pubOn : styles.pubOff) }}
          onClick={() => setPublished((p) => !p)}
          type="button"
          title={published ? "Rendre brouillon (masquer des élèves)" : "Publier (visible des élèves inscrits)"}
        >
          <Icon name="status" size={9} />
          {published ? "Publié" : "Brouillon"}
        </button>
      </div>

      <label style={styles.label}>Titre</label>
      <input style={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} />

      <label style={styles.label}>Description</label>
      <textarea style={{ ...styles.input, minHeight: 70, resize: "vertical" }} value={desc} onChange={(e) => setDesc(e.target.value)} />

      <label style={styles.label}>Degré(s) — clique pour attribuer à une ou plusieurs années</label>
      <div style={styles.toggleGroup}>
        {degrees.length === 0 && <span style={styles.empty}>Aucun degré. Crée-en via « Degrés &amp; chapitres ».</span>}
        {degrees.map((d) => {
          const on = degreeIds.includes(d.id);
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => toggleDegree(d.id)}
              style={{ ...styles.toggleBtn, ...(on ? styles.toggleBtnOn : {}) }}
              aria-pressed={on}
            >
              {on && <Icon name="done" size={13} />}
              {d.label}
            </button>
          );
        })}
      </div>

      <label style={styles.label}>Chapitre(s)</label>
      <TagMultiSelect
        options={chapters.map((c) => ({ id: c.id, title: c.title }))}
        selected={chapterIds}
        onChange={setChapterIds}
        onCreate={async (t) => { const ch = await onChapterCreated(t); return { id: ch.id, title: ch.title }; }}
        placeholder="Rechercher ou créer un chapitre…"
      />
      {/* Source de vérité unique : ici on ajoute/sélectionne ; renommer/supprimer un
          chapitre se fait dans la modale « Degrés & chapitres ». */}
      <button type="button" style={styles.manageLink} onClick={onManageTaxonomy}>
        Gérer les chapitres &amp; degrés…
      </button>

      {/* Étapes */}
      <div style={styles.stepsHead}>
        <h3 style={styles.h3}>Exercices</h3>
        <span style={{ flex: 1 }} />
        {steps.length > 0 && (
          <button style={styles.ghostBtn} onClick={toggleAll} type="button">
            {allOpen ? "Tout replier" : "Tout déplier"}
          </button>
        )}
      </div>
      {steps.length === 0 && <p style={styles.empty}>Aucun exercice. Ajoute-en un ci-dessous.</p>}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {steps.map((step, i) => (
              <StepCard
                key={step.id}
                step={step}
                index={i}
                total={steps.length}
                expanded={open.has(step.id)}
                packs={packs}
                track={track}
                onToggle={() => toggleOne(step.id)}
                onPatch={(patch) => patchStep(step.id, patch)}
                onMove={(dir) => moveStep(i, dir)}
                onDuplicate={() => dupStep(step.id)}
                onDelete={() => removeStep(step.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button style={{ ...styles.ghostBtn, marginTop: 12, alignSelf: "flex-start" }} onClick={addStep} type="button">
        + Ajouter un exercice
      </button>
    </div>
  );
}

// ─── Carte d'étape (en-tête + éditeur déplié en ligne) ─────────────────────────

const VAL_LABEL: Record<InstructorStep["validation_type"], string> = {
  auto_test: "auto", manual: "manuel", both: "auto + manuel",
};

function StepCard({
  step, index, total, expanded, packs, track, onToggle, onPatch, onMove, onDuplicate, onDelete,
}: {
  step: InstructorStep;
  index: number;
  total: number;
  expanded: boolean;
  packs: GamePack[];
  track: (p: Promise<unknown>) => Promise<void>;
  onToggle: () => void;
  onPatch: (patch: Partial<InstructorStep>) => void;
  onMove: (dir: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const gamelibEnabled = Boolean(user?.features?.gamelib);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: step.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    ...styles.stepCard,
  };

  // Enregistrement auto des champs de l'étape (les blocs se sauvent eux-mêmes).
  useDebouncedEffect(() => {
    track(updateStep(step.id, {
      title: step.title,
      starter_code: step.starter_code,
      validation_type: step.validation_type,
      test_code: step.test_code,
      solution_code: step.solution_code,
      scaffold_pre: step.scaffold_pre,
      scaffold_post: step.scaffold_post,
      game_pack_id: step.game_pack_id,
    }));
  }, [step.title, step.starter_code, step.validation_type, step.test_code, step.solution_code, step.scaffold_pre, step.scaffold_post, step.game_pack_id]);

  // « Tester le test » : exécute `test_code` contre une candidate (solution ou
  // starter). La solution canonique doit passer ; le starter, en général, doit
  // échouer — sinon le test ne discrimine pas.
  const kernel = useKernel();
  const toast  = useToast();
  // Le résultat brut du dernier test (passé/erreur) est affiché en bandeau juste
  // sous les boutons — utile car cette page n'a pas de console visible (≠ aperçu
  // élève). On capture la dernière entrée d'erreur du flux output via un ref
  // (kernel.output au moment du clic est figé par la closure).
  const outputRef = useRef(kernel.output);
  outputRef.current = kernel.output;
  const [testFeedback, setTestFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [testRunning, setTestRunning] = useState(false);

  const testTest = async (candidate: string, kind: "solution" | "starter") => {
    if (!kernel.ready) { toast.error("Kernel Pyodide pas encore prêt."); return; }
    setTestFeedback(null);
    setTestRunning(true);
    const beforeLen = outputRef.current.length;

    // Reproduit fidèlement l'environnement élève : scaffold pre/post + pack
    // monté dans /lib/pymods/<name>/. Sans ça, un `test_code` qui dépend de
    // l'état du scaffold (ex. `position("hero")` créé dans scaffold_pre)
    // remonterait une erreur trompeuse côté prof.
    let scaffold: Scaffold | undefined;
    const hasScaffold = !!(step.scaffold_pre || step.scaffold_post || step.game_pack_id);
    if (hasScaffold) {
      let packData: Scaffold["pack"];
      if (step.game_pack_id) {
        try {
          const { pack } = await getStepPack(step.id);
          packData = pack ?? undefined;
        } catch {
          // Si le fetch échoue (pas un blocage : le scaffold tournera quand même
          // si le pack n'a pas d'effet sur le test_code), on prévient discrètement.
          console.warn("[instructor] Pack non récupéré pour le test contre solution.");
        }
      }
      scaffold = {
        pre:  step.scaffold_pre  ?? "",
        post: step.scaffold_post ?? "",
        pack: packData,
      };
    }

    try {
      const ok = await kernel.runTest(candidate, step.test_code ?? "", scaffold);
      // Laisse React appliquer les setState issues des messages worker (output)
      // avant de lire le ref.
      await new Promise((r) => setTimeout(r, 0));
      const lastErr = [...outputRef.current.slice(beforeLen)].reverse()
        .find((o) => o.type === "error" || o.type === "stderr");
      if (kind === "solution") {
        if (ok) setTestFeedback({ ok: true, msg: "La solution passe le test ✓" });
        else    setTestFeedback({ ok: false, msg: lastErr?.text || "La solution ne passe pas le test." });
      } else {
        if (ok) setTestFeedback({ ok: false, msg: "Le code de démarrage passe — le test ne discrimine pas." });
        else    setTestFeedback({ ok: true,  msg: "Le code de démarrage échoue ✓ (le test discrimine bien)." });
      }
    } finally {
      setTestRunning(false);
    }
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={styles.stepHead}>
        <button style={styles.handle} {...attributes} {...listeners} aria-label="Déplacer l'étape" type="button">
          <DragDots />
        </button>
        <div style={styles.arrows}>
          <button style={styles.arrow} onClick={() => onMove(-1)} disabled={index === 0} aria-label="Monter" type="button"><Icon name="up" size={13} /></button>
          <button style={styles.arrow} onClick={() => onMove(1)} disabled={index === total - 1} aria-label="Descendre" type="button"><Icon name="down" size={13} /></button>
        </div>
        <span style={styles.stepNum}>{index + 1}</span>
        <input
          style={styles.stepTitle}
          value={step.title ?? ""}
          placeholder="Titre de l'exercice"
          onChange={(e) => onPatch({ title: e.target.value })}
        />
        <span style={styles.badge}>{VAL_LABEL[step.validation_type]}</span>
        <button style={styles.stepAction} onClick={onDuplicate} aria-label="Dupliquer l'exercice" title="Dupliquer" type="button"><Icon name="duplicate" size={15} /></button>
        <button style={styles.stepAction} onClick={onDelete} aria-label="Supprimer l'exercice" title="Supprimer" type="button"><Icon name="trash" size={14} /></button>
        <button style={styles.stepAction} onClick={onToggle} aria-label={expanded ? "Replier" : "Déplier"} aria-expanded={expanded} type="button">
          <Icon name={expanded ? "expanded" : "collapsed"} size={16} />
        </button>
      </div>

      {expanded && (
        <div style={styles.stepBody}>
          <label style={styles.label}>Consigne (blocs : texte / indice / image / fichier)</label>
          <BlockEditor stepId={step.id} blocks={step.blocks} onChange={(blocks) => onPatch({ blocks })} />

          <label style={styles.label}>Code de démarrage (affiché à l'élève)</label>
          <div style={styles.codeBox}>
            <Editor value={step.starter_code} onChange={(v) => onPatch({ starter_code: v })} height="150px" theme={theme === "light" ? "vs" : "vs-dark"} />
          </div>

          <label style={styles.label}>Type de validation</label>
          <select
            style={styles.input}
            value={step.validation_type}
            onChange={(e) => onPatch({ validation_type: e.target.value as InstructorStep["validation_type"] })}
          >
            <option value="auto_test">Automatique (test Python)</option>
            <option value="manual">Manuelle (enseignant)</option>
            <option value="both">Les deux</option>
          </select>

          {(step.validation_type === "auto_test" || step.validation_type === "both") && (
            <>
              <label style={styles.label}>Code de test (caché à l'élève)</label>
              <div style={{ ...styles.codeBox, height: 180 }}>
                <Editor value={step.test_code} onChange={(v) => onPatch({ test_code: v })} height="180px" theme={theme === "light" ? "vs" : "vs-dark"} />
              </div>

              {/* Exemples de patterns de test (pliable, statique — purement informatif).
                  Ces blocs ne s'exécutent pas, ils servent juste de cheatsheet. */}
              <details style={styles.helpBox}>
                <summary style={styles.helpSummary}>Exemples — patterns de test (cheatsheet)</summary>
                <div style={styles.helpBody}>
                  Le <code>test_code</code> est exécuté <strong>après</strong> le code de l'élève dans le même namespace.
                  Les variables/fonctions qu'il définit sont donc accessibles directement. La variable spéciale
                  <code> __student_code__</code> contient son source brut (chaîne), pour analyse AST ou exécution isolée.

                  <div style={styles.helpEgTitle}>① Assert simple — le cas courant</div>
                  <pre style={styles.helpCode}>{`# Suppose que l'élève définit une fonction \`carre\`
assert carre(3) == 9
assert carre(-4) == 16
assert carre(0) == 0`}</pre>

                  <div style={styles.helpEgTitle}>② Analyse AST — imposer/interdire une structure</div>
                  <pre style={styles.helpCode}>{`import ast

arbre = ast.parse(__student_code__)
if not [n for n in ast.walk(arbre) if isinstance(n, ast.For)]:
    raise AssertionError("Utilise une boucle 'for'.")`}</pre>

                  <div style={styles.helpEgTitle}>③ Exécution isolée — vérifier des variables sans polluer le namespace</div>
                  <pre style={styles.helpCode}>{`memoire = {}
exec(__student_code__, {}, memoire)
assert memoire.get("carres") == [0, 1, 4, 9, 16]`}</pre>
                </div>
              </details>

              <label style={styles.label}>Solution canonique (cachée — sert à valider le test)</label>
              <div style={{ ...styles.codeBox, height: 150 }}>
                <Editor value={step.solution_code} onChange={(v) => onPatch({ solution_code: v })} height="150px" theme={theme === "light" ? "vs" : "vs-dark"} />
              </div>
              <div style={styles.testRow}>
                <Button variant="primary" size="sm"
                        onClick={() => testTest(step.solution_code ?? "", "solution")}
                        disabled={!kernel.ready || testRunning}>
                  <Icon name="run" size={13} /> Tester contre ma solution
                </Button>
                <Button variant="secondary" size="sm"
                        onClick={() => testTest(step.starter_code ?? "", "starter")}
                        disabled={!kernel.ready || testRunning}>
                  <Icon name="run" size={13} /> Tester contre le code de démarrage
                </Button>
                {!kernel.ready && <span style={styles.kernelHint}>Pyodide se charge…</span>}
                {testRunning && <span style={styles.kernelHint}>Test en cours… (boucle d'anim : ~3 s max)</span>}
              </div>

              {/* Feedback inline du dernier « Tester contre … » — cette page n'a
                  pas de console visible, on affiche le traceback ici directement. */}
              {testFeedback && !testRunning && (
                <div style={testFeedback.ok ? styles.testOk : styles.testErr}>
                  <Icon name={testFeedback.ok ? "done" : "alert"} size={14}
                        style={{ color: testFeedback.ok ? "var(--green)" : "var(--red)", flexShrink: 0, marginTop: 2 }} />
                  <pre style={styles.testText}>{testFeedback.msg}</pre>
                </div>
              )}
            </>
          )}

          {/* Scaffold prof (caché à l'élève) : code injecté autour du code élève à
              chaque exécution. Réservé aux comptes avec le feature flag `gamelib`
              (cf. plan P0 — BETA_GAMELIB_USERS côté backend). */}
          {gamelibEnabled && (
            <>
              <details style={styles.helpBox}>
                <summary style={styles.helpSummary}>Scaffold prof (caché à l'élève — gamelib)</summary>
                <div style={styles.helpBody}>
                  À l'exécution côté élève, le code joué est <code>scaffold_pre + code élève + scaffold_post</code>.
                  L'élève ne voit ni le <code>pre</code> ni le <code>post</code> (ni dans l'éditeur, ni dans
                  l'API publique). Utilisé pour mettre en scène un jeu/contexte sans dévoiler les <code>import</code>.
                </div>
              </details>

              <label style={styles.label}>Scaffold avant (caché — exécuté avant le code élève)</label>
              <div style={{ ...styles.codeBox, height: 120 }}>
                <Editor value={step.scaffold_pre} onChange={(v) => onPatch({ scaffold_pre: v })} height="120px" theme={theme === "light" ? "vs" : "vs-dark"} />
              </div>

              <label style={styles.label}>Scaffold après (caché — exécuté après le code élève)</label>
              <div style={{ ...styles.codeBox, height: 120 }}>
                <Editor value={step.scaffold_post} onChange={(v) => onPatch({ scaffold_post: v })} height="120px" theme={theme === "light" ? "vs" : "vs-dark"} />
              </div>

              {/* Pack de jeu : si attaché, ses fichiers sont montés dans
                  /lib/pymods/<pack.name>/ avant le run (cf. plan gamelib P1.5).
                  Liste vide → message d'incitation à créer un pack depuis la fiche cours. */}
              <label style={styles.label}>Pack de jeu (optionnel — fichiers Python du cours montés à l'exécution)</label>
              {packs.length === 0 ? (
                <p style={styles.empty}>Aucun pack dans ce cours. Crée-en un depuis la fiche cours.</p>
              ) : (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select
                    style={{ ...styles.input, flex: 1 }}
                    value={step.game_pack_id ?? ""}
                    onChange={(e) => onPatch({ game_pack_id: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">— aucun —</option>
                    {packs.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  {/* Accès direct à l'éditeur du pack sélectionné (fichiers Python) —
                      évite de repasser par la fiche cours pour le modifier. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!step.game_pack_id}
                    onClick={() => step.game_pack_id && navigate(`/instructor/c/${courseId}/packs/${step.game_pack_id}`)}
                    title={step.game_pack_id ? "Modifier le pack de jeu (fichiers Python)" : "Sélectionne un pack pour le modifier"}
                  >
                    <Icon name="rename" size={15} /> Modifier
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SaveStatus({ status }: { status: "idle" | "saving" | "saved" }) {
  if (status === "idle") return null;
  return (
    <span style={styles.saveStatus}>
      {status === "saving" ? (
        "Enregistrement…"
      ) : (
        <>Enregistré <Icon name="done" size={13} style={{ color: "var(--green)" }} /></>
      )}
    </span>
  );
}

function DragDots() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {[6, 12, 18].flatMap((y) => [8, 16].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />))}
    </svg>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  // Remplit le <main> (mode focus) d'InstructorShell : pas de hauteur fixe propre.
  page: { display: "flex", flexDirection: "column", flex: 1, minHeight: 0, background: "var(--bg-base)", overflow: "hidden" },
  toolbar: {
    display: "flex", alignItems: "center", gap: 10, padding: "8px 16px",
    background: "var(--bg-bar)", borderBottom: "1px solid var(--border-soft)", flexShrink: 0, minHeight: 49,
  },
  crumbs: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  crumbLink: { background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 13.5, fontWeight: 600, padding: 0, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  crumbCurrent: { color: "var(--text)", fontSize: 13.5, fontWeight: 700 },
  manageBtn: { display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: "var(--text-muted)", padding: "5px 10px", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" },
  body: { display: "flex", flex: 1, overflow: "hidden" },

  sidebar: { width: 270, background: "var(--bg-bar)", borderRight: "1px solid var(--border-soft)", display: "flex", flexDirection: "column", flexShrink: 0 },
  sidebarHead: { padding: "14px 16px 6px" },
  newBtn: { margin: "0 10px 8px", width: "calc(100% - 20px)" },
  searchRow: { display: "flex", alignItems: "center", gap: 6, margin: "0 10px 8px", background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "4px 8px" },
  searchInput: { flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none", color: "var(--text)", fontSize: 13, fontFamily: "inherit" },
  filterBtn: { background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text-muted)", cursor: "pointer", padding: "2px 6px", display: "inline-flex", flexShrink: 0 },
  filters: { margin: "0 10px 8px", display: "flex", flexDirection: "column", gap: 4 },
  filterLabel: { fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text-muted)", marginTop: 2 },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 5 },
  chip: { background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: 999, color: "var(--text-muted)", fontSize: 11.5, fontWeight: 600, padding: "3px 9px", cursor: "pointer" },
  chipOn: { background: "var(--badge-bg)", borderColor: "var(--blue)", color: "var(--blue)" },
  viewToggle: { display: "flex", gap: 4, margin: "0 10px 8px", background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 3 },
  viewBtn: { flex: 1, background: "transparent", border: "none", borderRadius: "var(--radius-sm)", color: "var(--text-muted)", padding: "5px 8px", cursor: "pointer", fontSize: 12.5, fontWeight: 600 },
  viewBtnOn: { background: "var(--badge-bg)", color: "var(--blue)" },
  chGroup: { marginBottom: 4 },
  chHead: { display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left", background: "transparent", border: "none", color: "var(--text)", padding: "8px 6px", cursor: "pointer", fontSize: 12.5, fontWeight: 700 },
  chDragHandle: { background: "transparent", border: "none", color: "var(--text-faint)", cursor: "grab", padding: 0, display: "inline-flex", touchAction: "none", flexShrink: 0 },
  chCollapseBtn: { background: "transparent", border: "none", padding: 0, display: "inline-flex", cursor: "pointer", flexShrink: 0 },
  chNum: { width: 19, height: 19, borderRadius: "50%", background: "var(--badge-bg)", color: "var(--blue)", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  chTitle: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" },
  chTitleInput: { flex: 1, minWidth: 0, background: "var(--bg-base)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 12.5, fontWeight: 700, padding: "3px 6px", fontFamily: "inherit" },
  chCount: { flexShrink: 0, fontSize: 11, fontWeight: 700, color: "var(--text-muted)", background: "var(--bg-panel)", borderRadius: 999, padding: "1px 8px" },
  chEye: { background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 3, display: "inline-flex", borderRadius: "var(--radius-sm)", flexShrink: 0 },
  exList: { flex: 1, overflowY: "auto", padding: "0 6px 8px" },
  exItem: { display: "flex", alignItems: "center", gap: 4, padding: "6px 6px", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13 },
  exItemActive: { background: "var(--bg-panel)" },
  exHandle: { background: "transparent", border: "none", color: "var(--text-faint)", cursor: "grab", padding: 2, display: "inline-flex", touchAction: "none" },
  exTitle: { flex: 1, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  renameInput: { flex: 1, background: "var(--bg-base)", border: "1px solid var(--blue)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13, padding: "3px 6px", fontFamily: "inherit" },
  draft: { color: "var(--text-muted)", fontSize: 11 },
  exAction: { background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 3, display: "inline-flex" },
  exMenu: { position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 60, background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", boxShadow: "var(--shadow-1)", padding: 4, minWidth: 150 },
  exMenuDanger: { display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: "transparent", border: "none", borderRadius: "var(--radius-sm)", color: "var(--red)", padding: "8px 10px", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" },

  main: { flex: 1, overflowY: "auto" },
  editor: { padding: "24px 32px", maxWidth: 860, display: "flex", flexDirection: "column" },
  editorHead: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 },
  saveStatus: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--text-muted)" },
  h2: { color: "var(--blue)", margin: 0, fontSize: 18, fontWeight: 600 },
  h3: { color: "var(--blue)", margin: 0, fontSize: 16, fontWeight: 600 },
  stepsHead: { display: "flex", alignItems: "center", gap: 8, marginTop: 32, marginBottom: 12 },
  label: { display: "block", color: "var(--text-muted)", fontSize: 12, marginBottom: 4, marginTop: 14 },
  manageLink: { alignSelf: "flex-start", marginTop: 6, background: "transparent", border: "none", color: "var(--blue)", cursor: "pointer", fontSize: 12.5, fontWeight: 600, padding: 0 },
  input: { display: "block", width: "100%", background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "8px 10px", color: "var(--text)", fontSize: 13, fontFamily: "inherit" },
  ghostBtn: { background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: "var(--text)", padding: "6px 12px", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" },
  empty: { color: "var(--text-faint)", fontSize: 13, fontStyle: "italic" },

  // En-tête éditeur : aperçu + bascule de publication colorée.
  previewBtn: { display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: "var(--text-muted)", padding: "5px 10px", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" },
  pubToggle: { display: "inline-flex", alignItems: "center", gap: 6, borderRadius: "var(--radius)", padding: "5px 12px", cursor: "pointer", fontSize: 13, fontWeight: 600, border: "1px solid transparent", whiteSpace: "nowrap" },
  pubOn:  { background: "var(--ok-box-bg)", color: "var(--green)", borderColor: "var(--green)" },
  pubOff: { background: "var(--err-box-bg)", color: "var(--red)", borderColor: "var(--red)" },
  pubDisabled: { opacity: 0.5, cursor: "not-allowed", background: "transparent", color: "var(--text-muted)", borderColor: "var(--border)" },

  // Degrés en boutons à bascule.
  toggleGroup: { display: "flex", flexWrap: "wrap", gap: 8 },
  toggleBtn: { display: "inline-flex", alignItems: "center", gap: 6, background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: 999, color: "var(--text-muted)", padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  toggleBtnOn: { background: "var(--badge-bg)", borderColor: "var(--blue)", color: "var(--blue)" },
  warnHint: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-muted)", marginTop: 8 },

  // État vide centré.
  emptyState: { height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, textAlign: "center", padding: 24 },
  emptyTitle: { margin: 0, fontSize: 17, fontWeight: 600, color: "var(--text)" },
  emptyHint: { margin: "0 0 8px", fontSize: 13.5, color: "var(--text-muted)" },

  stepCard: { background: "var(--bg-bar)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius)", overflow: "hidden" },
  stepHead: { display: "flex", alignItems: "center", gap: 8, padding: "8px 10px" },
  handle: { background: "transparent", border: "none", color: "var(--text-faint)", cursor: "grab", padding: 2, display: "inline-flex", touchAction: "none" },
  arrows: { display: "flex", flexDirection: "column" },
  arrow: { background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 0, display: "inline-flex", lineHeight: 1 },
  stepNum: { minWidth: 18, textAlign: "center", fontWeight: 700, color: "var(--text-muted)", fontSize: 13 },
  stepTitle: { flex: 1, background: "transparent", border: "1px solid transparent", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 14, fontWeight: 600, padding: "4px 6px", fontFamily: "inherit" },
  badge: { fontSize: 11, color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap" },
  stepAction: { background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 4, display: "inline-flex" },
  stepBody: { padding: "4px 14px 16px", borderTop: "1px solid var(--border-soft)", display: "flex", flexDirection: "column" },
  codeBox: { height: 150, border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" },
  helpBox: { marginTop: 8, background: "var(--bg-bar)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius)", padding: "8px 12px" },
  helpSummary: { cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)", listStyle: "revert" },
  helpBody: { marginTop: 8, fontSize: 13, color: "var(--text)", lineHeight: 1.5 },
  helpEgTitle: { marginTop: 14, fontSize: 12.5, fontWeight: 700, color: "var(--text-muted)" },
  helpCode: { margin: "6px 0 0", padding: "10px 12px", background: "var(--bg-base)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius-sm)", fontFamily: "var(--font-mono)", fontSize: 12.5, lineHeight: 1.5, whiteSpace: "pre", overflow: "auto" },
  testRow: { display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" },
  kernelHint: { fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" },
  testOk:  { display: "flex", gap: 8, marginTop: 10, padding: "8px 10px", borderRadius: "var(--radius)", background: "var(--ok-box-bg)",  border: "1px solid var(--green)" },
  testErr: { display: "flex", gap: 8, marginTop: 10, padding: "8px 10px", borderRadius: "var(--radius)", background: "var(--err-box-bg)", border: "1px solid var(--red)" },
  testText: { margin: 0, fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: 1.4, color: "var(--text)", whiteSpace: "pre-wrap", wordBreak: "break-word", flex: 1 },
  preview: { marginTop: 8, height: 320, border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" },
};
